import { viewPaths } from '@better-auth-ui/core'
import { Auth } from '@better-auth-ui/heroui'
import { createFileRoute, notFound } from '@tanstack/react-router'
import { z } from 'zod'
import { PublicLayout } from '~/components/layout/public-layout'
import { CompleteAuthReturnTarget } from '~/features/auth/complete-auth-return-target'
import { RegistrationLegalNote } from '~/features/compliance/registration-legal-note'
import { seo } from '~/utils/seo'

export const Route = createFileRoute('/auth/$pathname')({
  validateSearch: z.object({
    returnTo: z.string().optional(),
  }),
  head: ({ params }) => {
    const title = authPageTitle(params.pathname, 'en')
    return seo({
      title: `${title} to BallPeek account`,
      description: authPageDescription(params.pathname, 'en'),
      pathname: `/auth/${params.pathname}`,
    })
  },
  beforeLoad({ params: { pathname } }): never {
    if (!Object.values(viewPaths.auth).includes(pathname)) {
      throw notFound()
    }
    return undefined as never
  },
  component: AuthPage,
})

function AuthPage() {
  const { pathname } = Route.useParams()
  const { returnTo } = Route.useSearch()

  // 映射 kbab-case 到 camelCase (如 'sign-in' -> 'signIn')
  const viewKey = pathname.replace(/-([a-z])/g, (_, group1) => group1.toUpperCase())

  return (
    <PublicLayout>
      <CompleteAuthReturnTarget returnTo={returnTo} />
      <div className="container mx-auto flex min-h-[calc(100vh-128px)] flex-col items-center justify-center p-4">
        <h1 className="sr-only">{authPageTitle(pathname, 'en')}</h1>
        <Auth view={viewKey as any} path={pathname} />
        {pathname === 'sign-up' && <RegistrationLegalNote />}
      </div>
    </PublicLayout>
  )
}

function authPageTitle(pathname: string, locale: 'en' | 'zh-CN') {
  const titles = locale === 'zh-CN'
    ? {
        'forgot-password': '找回密码',
        'reset-password': '重设密码',
        'sign-in': '登录',
        'sign-up': '注册',
        'verify-email': '验证邮箱',
      }
    : {
        'forgot-password': 'Forgot password',
        'reset-password': 'Reset password',
        'sign-in': 'Sign in',
        'sign-up': 'Sign up',
        'verify-email': 'Verify email',
      }

  return titles[pathname as keyof typeof titles] ?? (locale === 'zh-CN' ? '账户' : 'Account')
}

function authPageDescription(pathname: string, locale: 'en' | 'zh-CN') {
  const descriptions = locale === 'zh-CN'
    ? {
        'forgot-password': '输入邮箱找回密码，然后返回原来的账户任务。',
        'reset-password': '设置新密码，然后返回原来的账户任务。',
        'sign-in': '登录以继续账户、偏好设置或管理任务。公开足球比赛和资料无需登录。',
        'sign-up': '创建账户以使用账户和偏好设置。公开足球比赛和资料无需注册。',
        'verify-email': '验证邮箱以继续账户任务。',
      }
    : {
        'forgot-password': 'Recover your password, then return to the account task you started.',
        'reset-password': 'Set a new password, then return to the account task you started.',
        'sign-in': 'Sign in to continue an account, preference, or administration task. Public football matches and context do not require an account.',
        'sign-up': 'Create an account for account and preference features. Public football matches and context do not require registration.',
        'verify-email': 'Verify your email to continue your account task.',
      }

  return descriptions[pathname as keyof typeof descriptions] ?? descriptions['sign-in']
}
